import React, { JSX, useCallback, useEffect, useState } from 'react'
import {
  UserGroupIcon,
  TrashIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
  PlusIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { Dropdown } from 'components/Dropdown'
import { Avatar } from 'components/Avatar'
import { useAuth } from 'context/user'
import { useEventDetail } from 'hooks/useEventDetail'
import { useEventAccess } from 'hooks/useEventAccess'
import { graphqlRequest, formatLocalDateTime, getInitials } from 'lib/helpers'
import {
  EventDetail,
  EventMember,
  EventMemberRole,
  DeletionResult
} from 'types/api'
import addEventMemberMutation from 'graphql/addEventMember.graphql'
import removeEventMemberMutation from 'graphql/removeEventMember.graphql'
import updateEventMemberRoleMutation from 'graphql/updateEventMemberRole.graphql'
import rotateOverlayTokensMutation from 'graphql/rotateOverlayTokens.graphql'

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const ROLE_PILL: Record<EventMemberRole, string> = {
  owner: 'bg-brand/15 text-brand ring-brand/30',
  editor: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30'
}

const RoleBadge = ({ role }: { role: EventMemberRole }): JSX.Element => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ${ROLE_PILL[role]}`}
  >
    <ShieldCheckIcon className='size-3' aria-hidden='true' />
    {role}
  </span>
)

const MemberRow = ({
  member,
  isMe,
  myDisplayName,
  myEmail,
  myPicture,
  canManage,
  busy,
  onChangeRole,
  onRemove
}: {
  member: EventMember
  isMe: boolean
  myDisplayName: string | undefined
  myEmail: string | undefined
  myPicture: string | undefined
  canManage: boolean
  busy: 'role' | 'remove' | null
  onChangeRole: (role: EventMemberRole) => void
  onRemove: () => void
}): JSX.Element => {
  const displayName = isMe
    ? (myDisplayName ?? member.displayName ?? null)
    : member.displayName
  const email = isMe
    ? (myEmail ?? member.email ?? null)
    : member.email
  const display = displayName ?? email ?? 'Unknown user'
  const initials = getInitials(displayName ?? email)

  const showRemove = canManage || isMe

  const roleControl = (
    <div className='w-32'>
      <Dropdown
        value={member.role}
        disabled={busy !== null}
        options={[
          { value: 'editor', label: 'Editor' },
          { value: 'owner', label: 'Owner' }
        ]}
        onChange={(v) => onChangeRole(v as EventMemberRole)}
      />
    </div>
  )

  return (
    <li
      data-testid={`event-member-${member.userId}`}
      className='flex flex-col gap-3 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-3 sm:flex-row sm:items-center'
    >
      <div className='flex min-w-0 flex-1 items-center gap-3'>
        {isMe
          ? (
            <Avatar
              name={myDisplayName ?? member.displayName ?? undefined}
              email={myEmail ?? member.email ?? undefined}
              picture={myPicture}
              className='h-9 w-9 text-xs'
            />
            )
          : (
            <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/15 text-xs font-semibold text-brand'>
              {initials}
            </div>
            )}
        <div className='flex min-w-0 flex-col leading-tight'>
          <div className='flex items-center gap-2'>
            <span className='truncate text-sm font-medium text-dm-text-primary'>{display}</span>
            {isMe && (
              <span className='rounded bg-white/5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
                You
              </span>
            )}
          </div>
          {member.email !== null && member.email !== display && (
            <span className='truncate text-[11px] text-dm-text-accent'>{member.email}</span>
          )}
          <span className='truncate text-[10px] text-dm-text-accent'>
            Added {formatLocalDateTime(member.addedAt)}
          </span>
        </div>
      </div>

      <div className='flex shrink-0 items-center gap-2'>
        {canManage && !isMe ? roleControl : <RoleBadge role={member.role} />}
        {showRemove && (
          <button
            type='button'
            onClick={onRemove}
            disabled={busy !== null}
            title={isMe ? 'Leave stream' : 'Remove from stream'}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'remove'
              ? <LoadingSpinner />
              : <TrashIcon className='size-3.5' aria-hidden='true' />}
            {isMe ? 'Leave' : 'Remove'}
          </button>
        )}
      </div>
    </li>
  )
}

const AddMemberForm = ({
  busy,
  error,
  onSubmit,
  onDismissError
}: {
  busy: boolean
  error: string | null
  onSubmit: (email: string, role: EventMemberRole) => Promise<void>
  onDismissError: () => void
}): JSX.Element => {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<EventMemberRole>('editor')

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    const trimmed = email.trim().toLowerCase()
    if (trimmed.length === 0 || busy) return
    await onSubmit(trimmed, role)
    setEmail('')
    setRole('editor')
  }

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      className='space-y-3'
      data-testid='add-event-member-form'
    >
      <div className='flex flex-col gap-3 md:flex-row md:items-end'>
        <div className='flex-1'>
          <label className='mb-1 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
            Email
          </label>
          <div className='relative'>
            <EnvelopeIcon className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-dm-text-accent' aria-hidden='true' />
            <input
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='teammate@example.com'
              disabled={busy}
              className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 py-2 pl-9 pr-3 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
            />
          </div>
        </div>

        <div className='md:w-64'>
          <label className='mb-1 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
            Role
          </label>
          <Dropdown
            value={role}
            disabled={busy}
            options={[
              { value: 'editor', label: 'Editor' },
              { value: 'owner', label: 'Owner' }
            ]}
            onChange={(v) => setRole(v as EventMemberRole)}
          />
        </div>

        <button
          type='submit'
          disabled={busy || email.trim().length === 0}
          className='inline-flex items-center justify-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto'
        >
          {busy ? <LoadingSpinner /> : <PlusIcon className='size-4' aria-hidden='true' />}
          Add member
        </button>
      </div>

      <p className='text-[11px] text-dm-text-accent'>
        The teammate must sign up for FlightDeck previously to being invited.
      </p>

      {error !== null && (
        <ErrorInline onDismiss={onDismissError}>{error}</ErrorInline>
      )}
    </form>
  )
}

type RemovalPhase = 'removing' | 'resetting'

const RemoveMemberDialog = ({
  memberName,
  phase,
  onConfirm,
  onCancel
}: {
  memberName: string
  phase: RemovalPhase | null
  onConfirm: (alsoResetKey: boolean) => void
  onCancel: () => void
}): JSX.Element => {
  const [alsoResetKey, setAlsoResetKey] = useState(false)
  const busy = phase !== null

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    globalThis.addEventListener('keydown', onKeyDown)
    return () => globalThis.removeEventListener('keydown', onKeyDown)
  }, [busy, onCancel])

  let confirmLabel = 'Remove'
  if (phase === 'removing') confirmLabel = 'Removing…'
  if (phase === 'resetting') confirmLabel = 'Resetting key…'

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
      data-testid='remove-member-dialog'
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby='remove-member-dialog-title'
        className='max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border border-white/10 bg-dm-bg-secondary p-6 shadow-2xl'
      >
        <h2 id='remove-member-dialog-title' className='text-lg font-semibold text-dm-text-primary'>
          Remove {memberName} from this stream?
        </h2>
        <p className='mt-2 text-sm text-dm-text-secondary'>
          They lose access to this stream right away. The stream key stays
          the same, so any overlay link they already copied keeps working
          until the key is reset.
        </p>

        <label className='mt-4 flex cursor-pointer items-start gap-2.5 rounded-md border border-white/10 bg-dm-bg-accent/30 p-3 text-sm text-dm-text-primary'>
          <input
            type='checkbox'
            checked={alsoResetKey}
            onChange={(e) => setAlsoResetKey(e.target.checked)}
            disabled={busy}
            data-testid='remove-member-reset-key'
            className='mt-0.5 size-4 shrink-0 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
          />
          <span>
            Also reset this stream&apos;s key (everyone re-pastes)
            <span className='mt-0.5 block text-[11px] text-dm-text-accent'>
              Turns off every copy of the key, including your own OBS.
            </span>
          </span>
        </label>

        <div className='mt-5 flex items-center justify-end gap-2'>
          <button
            type='button'
            onClick={onCancel}
            disabled={busy}
            className='rounded-md border border-white/10 px-4 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={() => onConfirm(alsoResetKey)}
            disabled={busy}
            data-testid='remove-member-confirm'
            className='inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-300 hover:border-red-500/70 hover:text-red-200 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy ? <LoadingSpinner /> : <TrashIcon className='size-4' aria-hidden='true' />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

const mergeAddedMember = (prev: EventDetail | null, added: EventMember): EventDetail | null => {
  if (prev === null) return prev
  if (prev.members.some((m) => m.userId === added.userId)) return prev
  return { ...prev, members: [...prev.members, added] }
}

const mergeRemovedMember = (prev: EventDetail | null, userId: string): EventDetail | null => {
  if (prev === null) return prev
  return { ...prev, members: prev.members.filter((m) => m.userId !== userId) }
}

const mergeRoleChange = (prev: EventDetail | null, updated: EventMember): EventDetail | null => {
  if (prev === null) return prev
  return {
    ...prev,
    members: prev.members.map((m) => m.userId === updated.userId ? updated : m)
  }
}

export const AccessPage = (): JSX.Element => {
  const { auth0Token, streamToolsUser } = useAuth()
  const { event, setEvent } = useEventDetail()

  const [addBusy, setAddBusy] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [rowBusy, setRowBusy] = useState<{ userId: string, kind: 'role' | 'remove' } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const [pendingRemoval, setPendingRemoval] = useState<EventMember | null>(null)
  const [removalPhase, setRemovalPhase] = useState<RemovalPhase | null>(null)
  const [keyResetNotice, setKeyResetNotice] = useState<{ ok: boolean, message: string } | null>(null)
  const closeRemoveDialog = useCallback((): void => setPendingRemoval(null), [])

  const currentUserId = streamToolsUser?.userId
  const { canManage } = useEventAccess()

  const ordered = [...event.members].sort((a, b) => {
    if (a.role !== b.role) return a.role === 'owner' ? -1 : 1
    const an = (a.displayName ?? a.email ?? a.userId).toLowerCase()
    const bn = (b.displayName ?? b.email ?? b.userId).toLowerCase()
    return an.localeCompare(bn)
  })

  const handleAdd = async (email: string, role: EventMemberRole): Promise<void> => {
    if (auth0Token === undefined || addBusy) return
    setAddBusy(true)
    setAddError(null)
    try {
      const { member } = await graphqlRequest<{ member: EventMember }>(
        addEventMemberMutation,
        { eventId: event.eventId, email, role },
        auth0Token
      )
      setEvent((prev) => mergeAddedMember(prev, member))
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add member')
    } finally {
      setAddBusy(false)
    }
  }

  const handleChangeRole = async (member: EventMember, role: EventMemberRole): Promise<void> => {
    if (auth0Token === undefined || rowBusy !== null) return
    setRowBusy({ userId: member.userId, kind: 'role' })
    setRowError(null)
    try {
      const { member: updated } = await graphqlRequest<{ member: EventMember }>(
        updateEventMemberRoleMutation,
        { eventId: event.eventId, userId: member.userId, role },
        auth0Token
      )
      setEvent((prev) => mergeRoleChange(prev, updated))
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to update role')
    } finally {
      setRowBusy(null)
    }
  }

  const removeMember = async (member: EventMember, alsoResetKey: boolean): Promise<void> => {
    if (auth0Token === undefined) return
    const who = member.displayName ?? member.email ?? 'The member'
    setRowBusy({ userId: member.userId, kind: 'remove' })
    setRemovalPhase('removing')
    setRowError(null)
    setKeyResetNotice(null)
    let removed = false
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        removeEventMemberMutation,
        { eventId: event.eventId, userId: member.userId },
        auth0Token
      )
      if (result.deleted) {
        removed = true
        setEvent((prev) => mergeRemovedMember(prev, member.userId))
      }
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to remove member')
    } finally {
      setRowBusy(null)
    }

    if (removed && alsoResetKey) {
      setRemovalPhase('resetting')
      try {
        await graphqlRequest<{ rotation: { eventId: string, overlayTokenVersion: number } }>(
          rotateOverlayTokensMutation, { eventId: event.eventId }, auth0Token
        )
        setKeyResetNotice({
          ok: true,
          message: `${who} was removed and this stream's key was reset. Copy the new link from the Overview page and re-paste it anywhere you use it.`
        })
      } catch (err) {
        const reason = err instanceof Error ? err.message : 'The reset did not go through.'
        setKeyResetNotice({
          ok: false,
          message: `${who} was removed, but the stream key was not reset (${reason}). You can reset it with Reset stream key on the Overview page.`
        })
      }
    }

    setRemovalPhase(null)
    setPendingRemoval(null)
  }

  const handleRemove = (member: EventMember): void => {
    if (auth0Token === undefined || rowBusy !== null || removalPhase !== null) return
    const isMe = member.userId === currentUserId
    if (isMe) {
      if (!globalThis.confirm('Leave this stream? You will lose access until someone re-invites you.')) return
      void removeMember(member, false)
      return
    }
    setKeyResetNotice(null)
    setPendingRemoval(member)
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      {canManage
        ? (
          <SectionCard title='Invite a member'>
            <AddMemberForm
              busy={addBusy}
              error={addError}
              onSubmit={handleAdd}
              onDismissError={() => setAddError(null)}
            />
          </SectionCard>
          )
        : (
          <SectionCard
            title='Invite a member'
            action={
              <span className='inline-flex items-center gap-1.5 rounded-md border border-amber-500/20 bg-amber-500/5 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-300'>
                Owner only
              </span>
          }
          >
            <p className='flex items-center gap-1.5 text-xs text-dm-text-secondary'>
              <PencilSquareIcon className='size-3.5 text-dm-text-accent' aria-hidden='true' />
              Ask an owner of this stream to invite teammates.
            </p>
          </SectionCard>
          )}

      <SectionCard
        title='Members'
        action={
          <span className='inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-brand/15 px-2 text-xs font-semibold text-brand'>
            {event.members.length}
          </span>
        }
      >
        {!canManage && (
          <div className='mb-3 inline-flex items-center gap-1.5 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-1.5 text-[11px] text-amber-300'>
            <InformationCircleIcon className='size-3.5' aria-hidden='true' />
            You&apos;re an editor on this stream. Only owners can invite or remove other members.
          </div>
        )}

        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {keyResetNotice !== null && (
          <div className='mb-3'>
            {keyResetNotice.ok
              ? (
                <div
                  role='status'
                  className='flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300'
                >
                  <CheckCircleIcon className='size-4 shrink-0' aria-hidden='true' />
                  <span className='flex-1'>{keyResetNotice.message}</span>
                </div>
                )
              : <ErrorInline onDismiss={() => setKeyResetNotice(null)}>{keyResetNotice.message}</ErrorInline>}
          </div>
        )}

        {ordered.length === 0
          ? (
            <div className='flex flex-col items-center gap-2 rounded-lg border border-dashed border-white/10 px-4 py-8 text-center text-sm text-dm-text-secondary'>
              <UserGroupIcon className='size-6 text-dm-text-accent' aria-hidden='true' />
              <p>No members yet.</p>
            </div>
            )
          : (
            <ul className='space-y-2' data-testid='event-members-list'>
              {ordered.map((m) => (
                <MemberRow
                  key={m.userId}
                  member={m}
                  isMe={m.userId === currentUserId}
                  myDisplayName={streamToolsUser?.name}
                  myEmail={streamToolsUser?.email}
                  myPicture={streamToolsUser?.picture}
                  canManage={canManage}
                  busy={rowBusy?.userId === m.userId ? rowBusy.kind : null}
                  onChangeRole={(role) => { void handleChangeRole(m, role) }}
                  onRemove={() => handleRemove(m)}
                />
              ))}
            </ul>
            )}
      </SectionCard>

      {pendingRemoval !== null && (
        <RemoveMemberDialog
          key={pendingRemoval.userId}
          memberName={pendingRemoval.displayName ?? pendingRemoval.email ?? 'this member'}
          phase={removalPhase}
          onConfirm={(alsoResetKey) => { void removeMember(pendingRemoval, alsoResetKey) }}
          onCancel={closeRemoveDialog}
        />
      )}
    </div>
  )
}
